import type { ProductGridModelValue } from "@/features/EmailEditor/plugins/productGridConstants";

import { PRODUCT_FIELDS } from "@/services/mergeTags";

function productItemTileDataPath(
  productModel: ProductGridModelValue,
  field: string,
): string {
  if (productModel === "discover") {
    return `tile_data.dynamic[0].items[0].${field}`;
  }
  return `tile_data.${productModel}[0].${field}`;
}

/**
 * Text Composer `{{ name }}` merge tags that map to `tile_data.*` in
 * `transformTextComposerProductMergeTags`.
 *
 * The product-data aliases are derived from the shared service `PRODUCT_FIELDS`
 * (add a product field there and it shows up here). Only the alias-token naming,
 * the `tile_data.<model>[0].<field>` resolution, and legacy non-field aliases
 * (`cart_url`) are Text-Composer-local.
 */
export interface TextComposerProductAliasDefinition {
  /** Inner token, e.g. `product_name` in `{{ product_name }}`. */
  readonly identifier: string;
  /** Label in the "Insert merge tag" / picker UI. */
  readonly label: string;
  /** Sample value shown inside the editor chip (e.g. "Acme Sneakers"). */
  readonly preview: string;
  /** Whether this alias represents a URL value or plain text. */
  readonly type: "text" | "url";
  readonly category: { readonly id: string; readonly label: string };
  readonly toTileData: (productModel: ProductGridModelValue) => string;
  /** Transforms legacy `{{ cart_url }}` drafts but is omitted from the picker. */
  readonly hiddenFromPicker?: boolean;
}

const PRODUCT_DATA_CATEGORY = {
  id: "product-data",
  label: "Product data",
} as const;

/**
 * Legacy alias tokens kept stable so existing drafts keep resolving; every other
 * field uses `product_<field>` (e.g. `product_price`, `product_image_url`).
 */
const FIELD_TOKEN_OVERRIDES: Record<string, string> = {
  item_url: "product_url",
};

/** Legacy alias identifier → product field, for the V1→V2 body migration. */
export const PRODUCT_ALIAS_FIELDS: ReadonlyArray<{
  identifier: string;
  field: string;
}> = PRODUCT_FIELDS.map((field) => ({
  identifier: FIELD_TOKEN_OVERRIDES[field.field] ?? `product_${field.field}`,
  field: field.field,
}));

const productDataAliases: TextComposerProductAliasDefinition[] =
  PRODUCT_FIELDS.map((field) => ({
    identifier: FIELD_TOKEN_OVERRIDES[field.field] ?? `product_${field.field}`,
    label: field.label,
    preview: field.preview,
    type: field.tagType,
    category: PRODUCT_DATA_CATEGORY,
    toTileData: (productModel: ProductGridModelValue) =>
      `{{ ${productItemTileDataPath(productModel, field.field)} }}`,
  }));

export const TEXT_COMPOSER_PRODUCT_ALIASES: readonly TextComposerProductAliasDefinition[] =
  [
    ...productDataAliases,
    {
      identifier: "cart_url",
      label: "Cart URL",
      preview: "https://example.com/cart",
      type: "url",
      category: { id: "cart-information", label: "Cart information" },
      toTileData: () => "{{ tile_data.return_url }}",
      hiddenFromPicker: true,
    },
  ];

function escapeRegExp(s: string): string {
  return s.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}

const TEXT_COMPOSER_ALIAS_IDENTIFIERS = TEXT_COMPOSER_PRODUCT_ALIASES.map(
  (a) => a.identifier,
);
const ALTERNATION = TEXT_COMPOSER_ALIAS_IDENTIFIERS.map(escapeRegExp).join("|");

/**
 * Same semantics as the previous `TEXT_COMPOSER_PRODUCT_ALIAS_PATTERN` in
 * `hasProductMergeTag` in `mergeTagDefinitions.ts` — see {@link TEXT_COMPOSER_PRODUCT_ALIASES}.
 */
export const TEXT_COMPOSER_PRODUCT_ALIAS_PATTERN = new RegExp(
  `\\{\\{\\s*(?:${ALTERNATION})\\s*\\}\\}`,
  "gi",
);

/**
 * Replaces Text Composer `{{ * }}` aliases with `tile_data.*` merge tags
 * (equivalent to the prior chained `.replace` implementation).
 */
export function transformTextComposerProductMergeTags(
  input: string,
  productModel: ProductGridModelValue,
): string {
  let out = input;
  for (const { identifier, toTileData } of TEXT_COMPOSER_PRODUCT_ALIASES) {
    const re = new RegExp(
      `\\{\\{\\s*${escapeRegExp(identifier)}\\s*\\}\\}`,
      "g",
    );
    out = out.replace(re, toTileData(productModel));
  }
  return out;
}
